import type { ReactNode } from 'react';
import { STAT_IDS } from '../../lib/hb/types';
import {
  ALLY_DISPOSITIONS,
  ALLY_OPS,
  DELAY_ON_RAID_END,
  EFFECT_BLOCKS,
  EFFECT_CAPS,
  EFFECT_CONDITION_IDS,
  EFFECT_RUNS,
  EFFECT_TYPES,
  END_AS,
  FLAG_SCOPES,
  GIVE_TO,
  OBJECTIVE_OPS,
  TIP_MARKERS,
  TIP_NEAR,
  TRADE_LIES,
  TRADE_RESULTS,
  newEffect,
  type EffectType,
} from '../../lib/story/effects';
import type { EffectDef } from '../../lib/story/types';
import { issuesUnder, type FieldErrors, type Issue } from '../../lib/validate';
import {
  CheckboxField,
  Field,
  IssueList,
  ListEditor,
  NumberField,
  SelectField,
  SelectInput,
  StackListEditor,
  TextField,
  inputCls,
} from '../forms/fields';
import { useContentBundle, useContentRefs } from '../forms/refs';

const opts = (list: readonly string[]) => list.map((v) => ({ value: v, label: v }));

const runLabel = { raid: 'raid', server: 'server', both: 'raid + server' } as const;

/**
 * Typed effect forms (engine.html §3 "Effects (v1 registry)", the `TriggerForms` pattern): a type select that swaps the one block
 * (`newEffect` drops every other block), then the fields of that type. `roles` are the chain's cast roles (plus player and leader).
 * Issues are keyed under `base` (`outcomes.oA.effects`).
 */
export function EffectListEditor({
  label,
  value,
  onChange,
  roles,
  errors,
  base,
  raidOnly,
  hint,
}: {
  label: string;
  value: EffectDef[];
  onChange: (next: EffectDef[]) => void;
  roles: string[];
  errors: FieldErrors;
  base: string;
  /** A delay's `then`: only raid effects, no nested delay. */
  raidOnly?: boolean;
  hint?: string;
}) {
  return (
    <ListEditor
      label={label}
      hint={hint}
      items={value}
      onChange={onChange}
      what="effect"
      issues={errors[base]}
      make={() => newEffect(raidOnly ? 'spawn' : 'give_items')}
      row={(e, i, set) => (
        <EffectRow
          value={e}
          onChange={set}
          roles={roles}
          errors={errors}
          base={`${base}.${i}`}
          raidOnly={raidOnly}
        />
      )}
    />
  );
}

function EffectRow({
  value,
  onChange,
  roles,
  errors,
  base,
  raidOnly,
}: {
  value: EffectDef;
  onChange: (next: EffectDef) => void;
  roles: string[];
  errors: FieldErrors;
  base: string;
  raidOnly?: boolean;
}) {
  const type = (value.type ?? '') as EffectType;
  const known = (EFFECT_TYPES as readonly string[]).includes(type);
  const blockKey = known ? EFFECT_BLOCKS[type] : undefined;
  const block = (blockKey ? (value[blockKey] ?? {}) : {}) as Record<string, unknown>;
  const set = (patch: Record<string, unknown>) =>
    blockKey && onChange({ ...value, [blockKey]: { ...block, ...patch } });
  const at = (f: string) => `${base}.${blockKey}.${f}`;
  const issues = (f: string): Issue[] => errors[at(f)] ?? [];
  const s = (f: string) => (block[f] as string | undefined) ?? '';
  const n = (f: string) => (block[f] as number | undefined) ?? 0;
  const types = EFFECT_TYPES.filter(
    (t) => !raidOnly || (EFFECT_RUNS[t] !== 'server' && t !== 'delay'),
  );
  const roleOptions = opts(roles);

  const text = (f: string, label: string, hint?: string) => (
    <TextField
      label={label}
      hint={hint}
      mono
      value={s(f)}
      issues={issues(f)}
      onChange={(v) => set({ [f]: v })}
    />
  );
  const number = (
    f: string,
    label: string,
    o: { integer?: boolean; min?: number; max?: number; hint?: string; nullable?: boolean } = {},
  ) => (
    <NumberField
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={o.min}
      max={o.max}
      nullable={o.nullable}
      value={o.nullable ? ((block[f] as number | null | undefined) ?? null) : n(f)}
      issues={issues(f)}
      onChange={(v) => set({ [f]: o.nullable ? v : (v ?? 0) })}
    />
  );
  const select = (
    f: string,
    label: string,
    list: { value: string; label: string }[],
    none?: string,
  ) => (
    <SelectField
      label={label}
      value={s(f)}
      options={list}
      none={none}
      issues={issues(f)}
      onChange={(v) => set({ [f]: v ?? '' })}
    />
  );
  const role = (f = 'role', label = 'Role') => select(f, label, roleOptions);

  let body: ReactNode = null;
  switch (type) {
    case 'give_items':
      body = (
        <>
          <StackListEditor
            label="Stacks (≤ 3, qty ≤ 10)"
            value={block.stacks as never}
            onChange={(v) => set({ stacks: v })}
            errors={errors}
            base={at('stacks')}
          />
          {select('to', 'To', opts(GIVE_TO))}
        </>
      );
      break;
    case 'take_items':
      body = (
        <>
          <StackListEditor
            label="Stacks"
            value={block.stacks as never}
            onChange={(v) => set({ stacks: v })}
            errors={errors}
            base={at('stacks')}
          />
          {text('tag', 'Or a tag', 'Any item with this tag (food), up to qty.')}
          {number('qty', 'Qty (tag)', { integer: true, min: 0, max: 10 })}
        </>
      );
      break;
    case 'trade':
      body = <TradeFields block={block} set={set} errors={errors} at={at} />;
      break;
    case 'reveal_stash':
      body = (
        <>
          {text('anchor', 'Anchor', "A set piece's container or locked-room anchor (dock_cage).")}
          <TableSelect
            value={s('table')}
            issues={issues('table')}
            onChange={(v) => set({ table: v })}
          />
          {number('rolls', 'Rolls', { integer: true, min: 1 })}
        </>
      );
      break;
    case 'injure':
      body = (
        <>
          {role()}
          {number('wind', 'Wind (hit wind, 0..100)', {
            min: 0,
            max: EFFECT_CAPS.injureWind,
            nullable: true,
            hint: 'WIND: a hit of this much wind. Empty = not set (the old HP is used).',
          })}
          {number('hp', 'HP (≤ 60, old)', {
            integer: true,
            min: 1,
            max: 60,
            hint: 'Old, kept one content version: wind replaces it.',
          })}
          <CheckboxField
            label="Down (a teammate, never the player)"
            checked={!!block.down}
            onChange={(v) => set({ down: v })}
          />
        </>
      );
      break;
    case 'condition':
      body = (
        <>
          {role()}
          {select('conditionId', 'Condition', opts(EFFECT_CONDITION_IDS))}
          {number('turnHours', 'Turn hours (bitten; 0 = default)', { min: 0 })}
        </>
      );
      break;
    case 'kill':
      body = role();
      break;
    case 'turn':
      body = (
        <>
          {role()}
          <ZombieSelect
            value={s('zombieId')}
            issues={issues('zombieId')}
            onChange={(v) => set({ zombieId: v })}
          />
          <CheckboxField
            label="Grab (its first attack can't miss)"
            checked={!!block.grab}
            onChange={(v) => set({ grab: v })}
          />
        </>
      );
      break;
    case 'recruit':
      body = (
        <>
          {role()}
          <RecruitRoleSelect
            value={s('roleId')}
            issues={issues('roleId')}
            onChange={(v) => set({ roleId: v })}
          />
        </>
      );
      break;
    case 'spawn':
      body = (
        <>
          <ZombieSelect
            value={s('zombieId')}
            issues={issues('zombieId')}
            onChange={(v) => set({ zombieId: v })}
          />
          {number('count', 'Count (≤ 8)', { integer: true, min: 1, max: 8 })}
          {text('at', 'At', 'room | door | near_role | anchor:<id>')}
          <CheckboxField
            label="Aware"
            checked={!!block.aware}
            onChange={(v) => set({ aware: v })}
          />
        </>
      );
      break;
    case 'wave':
      body = (
        <>
          {number('size', 'Size', { integer: true, min: 0, max: 25 })}
          {text('waveId', 'Or wave id', 'A holdout wave (Step 2).')}
          {text('from', 'From', 'anchor:<id>, or empty for the entrance.')}
        </>
      );
      break;
    case 'noise':
      body = (
        <>
          {number('radiusM', 'Radius m (≤ 60)', { min: 0, max: 60 })}
          {text('at', 'At', 'A role or anchor.')}
        </>
      );
      break;
    case 'objective':
      body = (
        <>
          {select('op', 'Op', opts(OBJECTIVE_OPS))}
          {text('objectiveId', 'Objective id')}
        </>
      );
      break;
    case 'flag':
      body = (
        <>
          <FlagKeyField value={s('key')} issues={issues('key')} onChange={(v) => set({ key: v })} />
          {text('value', 'Value', 'bool, int or string (≤ 32 characters).')}
          {select('scope', 'Scope', opts(FLAG_SCOPES))}
        </>
      );
      break;
    case 'mission':
      body = (
        <MissionSelect
          value={s('missionId')}
          issues={issues('missionId')}
          onChange={(v) => set({ missionId: v })}
        />
      );
      break;
    case 'tip':
      body = (
        <>
          {select('marker', 'Marker', opts(TIP_MARKERS))}
          {select('near', 'Near', opts(TIP_NEAR))}
          {number('maxDistanceM', 'Max distance m', { min: 0 })}
          <MissionSelect
            value={s('missionId')}
            issues={issues('missionId')}
            onChange={(v) => set({ missionId: v })}
            none="tip_cache (default)"
          />
        </>
      );
      break;
    case 'ally':
      body = (
        <>
          {select('op', 'Op', opts(ALLY_OPS))}
          <AllySelect
            value={s('allyId')}
            issues={issues('allyId')}
            onChange={(v) => set({ allyId: v })}
          />
          {role('role', 'Role (look and name)')}
          {select('disposition', 'Disposition', opts(ALLY_DISPOSITIONS))}
        </>
      );
      break;
    case 'boon':
      body = (
        <>
          {select('target', 'Target', [
            { value: 'team', label: 'team' },
            ...roles.map((r) => ({ value: `role:${r}`, label: `role:${r}` })),
          ])}
          {number('wind', 'Wind restored (0..100)', {
            min: 0,
            max: EFFECT_CAPS.boonWind,
            nullable: true,
            hint: 'WIND: wind given back. Empty = not set (the old heal is used).',
          })}
          {number('heal', 'Heal (≤ 40, old)', {
            integer: true,
            min: 0,
            max: 40,
            hint: 'Old, kept one content version: wind replaces it.',
          })}
          {select('clear', 'Clear', [{ value: 'wounded', label: 'wounded' }], '(nothing)')}
        </>
      );
      break;
    case 'delay':
      body = (
        <>
          {text('id', 'Id', 'Reported in the result (turn_close).')}
          {number('afterS', 'After s (3..120, raid time)', { min: 3, max: 120 })}
          {select('cancelIfDead', 'Cancel if dead', roleOptions, '(never)')}
          {select('onRaidEnd', 'On raid end', opts(DELAY_ON_RAID_END))}
          <div className="sm:col-span-2">
            <EffectListEditor
              label="Then (raid effects)"
              value={(block.then as EffectDef[] | undefined) ?? []}
              onChange={(v) => set({ then: v })}
              roles={roles}
              errors={errors}
              base={at('then')}
              raidOnly
            />
          </div>
        </>
      );
      break;
    case 'end':
      body = select('as', 'As', opts(END_AS));
      break;
    case 'train':
      body = (
        <>
          {select('role', 'Who', opts(['player', 'leader', ...roles]))}
          {select('stat', 'Stat', opts(STAT_IDS))}
          {number('marks', 'Marks (1..beatMarksMax)', { integer: true, min: 1, max: 2 })}
        </>
      );
      break;
    case 'apply_effect':
      body = (
        <>
          {text('id', 'Effect id', 'A statusEffects[] id that lists the beat trigger.')}
          {select(
            'role',
            'Target (survivor-scoped effects)',
            opts(['leader', ...roles]),
            '(its own scope)',
          )}
        </>
      );
      break;
    default:
      body = <p className="text-xs text-blam">Unknown effect type: pick one.</p>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <SelectInput
          label="Effect type"
          value={known ? type : (value.type ?? '')}
          options={types.map((t) => ({ value: t, label: `${t} · ${runLabel[EFFECT_RUNS[t]]}` }))}
          onChange={(v) => v && onChange(newEffect(v as EffectType))}
        />
      </div>
      <div className="mt-1 grid gap-2 sm:grid-cols-2">{body}</div>
      <IssueList
        issues={[
          ...(errors[`${base}.type`] ?? []),
          ...(blockKey ? (errors[`${base}.${blockKey}`] ?? []) : []),
        ]}
      />
      <OtherIssues
        errors={errors}
        base={base}
        shown={(p) => !!blockKey && p.startsWith(`${base}.${blockKey}.`)}
      />
    </div>
  );
}

/** Issues under this effect that no field shows (a stray block, a nested path). */
function OtherIssues({
  errors,
  base,
  shown,
}: {
  errors: FieldErrors;
  base: string;
  shown: (p: string) => boolean;
}) {
  const rest = Object.entries(errors)
    .filter(([p]) => p.startsWith(`${base}.`) && !shown(p) && p !== `${base}.type`)
    .flatMap(([, v]) => v);
  return <IssueList issues={rest} />;
}

function TradeFields({
  block,
  set,
  errors,
  at,
}: {
  block: Record<string, unknown>;
  set: (patch: Record<string, unknown>) => void;
  errors: FieldErrors;
  at: (f: string) => string;
}) {
  type Give = { itemId?: string; tag?: string; qty?: number };
  const give = (block.give as Give[] | undefined) ?? [];
  const s = (f: string) => (block[f] as string | undefined) ?? '';
  return (
    <>
      <ListEditor
        label="Give (≤ 2: an item or a tag)"
        items={give}
        onChange={(v) => set({ give: v })}
        what="given stack"
        issues={issuesUnder(errors, at('give'))}
        make={() => ({ itemId: '', tag: '', qty: 1 })}
        row={(g, _i, put) => (
          <div className="grid grid-cols-3 gap-1">
            <input
              aria-label="Give item"
              className={`${inputCls} font-mono`}
              placeholder="itemId"
              value={g.itemId ?? ''}
              onChange={(e) => put({ ...g, itemId: e.target.value })}
            />
            <input
              aria-label="Give tag"
              className={`${inputCls} font-mono`}
              placeholder="or tag"
              value={g.tag ?? ''}
              onChange={(e) => put({ ...g, tag: e.target.value })}
            />
            <input
              aria-label="Give qty"
              type="number"
              className={inputCls}
              value={g.qty ?? 0}
              onChange={(e) => put({ ...g, qty: Number(e.target.value) })}
            />
          </div>
        )}
      />
      <StackListEditor
        label="Promised (≤ 3)"
        value={block.promised as never}
        onChange={(v) => set({ promised: v })}
        errors={errors}
        base={at('promised')}
      />
      <SelectField
        label="Result"
        value={s('result')}
        options={opts(TRADE_RESULTS)}
        issues={errors[at('result')]}
        onChange={(v) => set({ result: v ?? 'honest' })}
        hint="Picked by the hidden truth: honest, generous (+ bonus), short, lying."
      />
      {s('result') === 'generous' && (
        <TableSelect
          label="Bonus table"
          value={s('bonusTable')}
          issues={errors[at('bonusTable')]}
          onChange={(v) => set({ bonusTable: v })}
        />
      )}
      {s('result') === 'lying' && (
        <>
          <SelectField
            label="Lie"
            value={s('lie')}
            options={opts(TRADE_LIES)}
            issues={errors[at('lie')]}
            onChange={(v) => set({ lie: v ?? '' })}
          />
          <TextField
            label="Junk item"
            mono
            value={s('junkItemId')}
            issues={errors[at('junkItemId')]}
            onChange={(v) => set({ junkItemId: v })}
            hint="Default: the cheapest material."
          />
        </>
      )}
    </>
  );
}

function TableSelect({
  value,
  onChange,
  issues,
  label = 'Loot table',
}: {
  value: string;
  onChange: (v: string) => void;
  issues?: Issue[];
  label?: string;
}) {
  const { lootTables } = useContentRefs();
  return (
    <SelectField
      label={label}
      value={value}
      options={lootTables.map((t) => ({ value: t.id ?? '', label: t.id ?? '' }))}
      issues={issues}
      onChange={(v) => onChange(v ?? '')}
    />
  );
}

function ZombieSelect({
  value,
  onChange,
  issues,
}: {
  value: string;
  onChange: (v: string) => void;
  issues?: Issue[];
}) {
  const { zombies } = useContentRefs();
  return (
    <SelectField
      label="Zombie"
      value={value}
      options={zombies.map((z) => ({ value: z.id ?? '', label: `${z.name ?? z.id} (${z.id})` }))}
      issues={issues}
      onChange={(v) => onChange(v ?? '')}
    />
  );
}

function MissionSelect({
  value,
  onChange,
  issues,
  none,
}: {
  value: string;
  onChange: (v: string) => void;
  issues?: Issue[];
  none?: string;
}) {
  const { missions } = useContentRefs();
  return (
    <SelectField
      label="Mission"
      value={value}
      none={none}
      options={missions.map((m) => ({ value: m.id ?? '', label: `${m.title || m.id} (${m.id})` }))}
      issues={issues}
      onChange={(v) => onChange(v ?? '')}
    />
  );
}

function AllySelect({
  value,
  onChange,
  issues,
}: {
  value: string;
  onChange: (v: string) => void;
  issues?: Issue[];
}) {
  const b = useContentBundle();
  const allies = (b.allies ?? []).filter((a) => a != null);
  return (
    <SelectField
      label="Ally"
      value={value}
      options={allies.map((a) => ({ value: a.id ?? '', label: `${a.name || a.id} (${a.id})` }))}
      issues={issues}
      onChange={(v) => onChange(v ?? '')}
    />
  );
}

function RecruitRoleSelect({
  value,
  onChange,
  issues,
}: {
  value: string;
  onChange: (v: string) => void;
  issues?: Issue[];
}) {
  const b = useContentBundle();
  // Recruit roles are retired (card 11): the recruit's kind is a background id.
  const backgrounds = (b.backgrounds ?? []).filter((g) => g != null);
  return (
    <SelectField
      label="Background"
      value={value}
      none="(the binding's own)"
      options={backgrounds.map((g) => ({ value: g.id ?? '', label: g.id ?? '' }))}
      issues={issues}
      onChange={(v) => onChange(v ?? '')}
    />
  );
}

/** A world flag key, with the catalogue's keys offered. */
function FlagKeyField({
  value,
  onChange,
  issues,
}: {
  value: string;
  onChange: (v: string) => void;
  issues?: Issue[];
}) {
  const b = useContentBundle();
  const keys = (b.worldFlags ?? []).map((f) => f?.key ?? '').filter(Boolean);
  return (
    <Field
      label="Flag key"
      issues={issues}
      hint={keys.length ? `Catalogue: ${keys.join(', ')}` : 'The world flag catalogue is empty.'}
    >
      {(id, invalid) => (
        <input
          id={id}
          aria-invalid={invalid}
          className={`${inputCls} font-mono`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Field>
  );
}
